import { normalizeMachineCloneStatus } from 'common/constants/machineCloneStatus';

/**
 * Shared, persistent store for machine-clone tasks.
 *
 * Two problems this solves, both reported from the field:
 *
 *  1. The list used to live in one tab's sessionStorage, private to the tab that
 *     started the clone. Open a new tab and a running 12 hour clone was
 *     untrackable, and there was no way to clear a finished one from the panel.
 *     localStorage plus an explicit dismiss fixes both.
 *  2. A demo clone started from the admin dashboard was only visible inside the
 *     Add Account modal, so closing the modal lost it and it never showed in the
 *     Asset Hierarchy panel. Both surfaces now read and write this one store.
 *
 * Tasks are keyed per user id. localStorage is shared across sessions on the same
 * browser, so an unscoped key would show one user's clone targets, including
 * account names, to the next person who logs in on that machine.
 *
 * NOTE: this is still browser-local. Tracking a clone from another device needs a
 * server-side record of the task, which does not exist yet.
 */

const STORAGE_KEY_PREFIX = 'petasenseCloneTasks.v1';

// A clone can legitimately run for 12 hours, so keep finished tasks around long
// enough to be seen the next working day, then stop hoarding them.
const TASK_TTL_MS = 48 * 60 * 60 * 1000;
const MAX_STORED_TASKS = 20;

const listeners = new Set();

export const cloneTaskStorageKey = userId => `${STORAGE_KEY_PREFIX}.${userId || 'anonymous'}`;

const isFresh = (task) => {
  if (!task || !task.startedAt) return true;
  const startedAtMs = new Date(task.startedAt).getTime();
  if (!Number.isFinite(startedAtMs)) return true;
  return Date.now() - startedAtMs < TASK_TTL_MS;
};

const normalizeTask = task => ({
  ...task,
  taskId: String(task.taskId),
  status: normalizeMachineCloneStatus(task.status),
  machineRows: Array.isArray(task.machineRows) ? task.machineRows : []
});

export const readCloneTasks = (userId) => {
  try {
    const raw = localStorage.getItem(cloneTaskStorageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(task => task && task.taskId)
      .filter(isFresh)
      .slice(0, MAX_STORED_TASKS)
      .map(normalizeTask);
  } catch (error) {
    return [];
  }
};

const notify = (userId, tasks) => {
  listeners.forEach((listener) => {
    try {
      listener(userId, tasks);
    } catch (error) {
      // A broken subscriber must not stop the others, or block clone polling.
    }
  });
};

export const writeCloneTasks = (userId, tasks, { silent = false } = {}) => {
  const trimmed = (tasks || [])
    .filter(task => task && task.taskId)
    .slice(0, MAX_STORED_TASKS)
    .map(normalizeTask);
  try {
    localStorage.setItem(cloneTaskStorageKey(userId), JSON.stringify(trimmed));
  } catch (error) {
    // Quota or private-mode failures must not block the clone itself.
  }
  if (!silent) notify(userId, trimmed);
  return trimmed;
};

export const upsertCloneTask = (userId, task) => {
  const existing = readCloneTasks(userId);
  const withoutTask = existing.filter(item => String(item.taskId) !== String(task.taskId));
  const previous = existing.find(item => String(item.taskId) === String(task.taskId));
  // Newest first, and an update keeps its original position at the top.
  return writeCloneTasks(userId, [{ ...previous, ...task }, ...withoutTask]);
};

export const removeCloneTask = (userId, taskId) => writeCloneTasks(
  userId,
  readCloneTasks(userId).filter(task => String(task.taskId) !== String(taskId))
);

/**
 * Fires on same-tab writes and on writes from other tabs. Returns an unsubscribe.
 */
export const subscribeToCloneTasks = (listener) => {
  listeners.add(listener);

  const onStorage = (event) => {
    if (!event.key || event.key.indexOf(STORAGE_KEY_PREFIX) !== 0) return;
    const userId = event.key.slice(STORAGE_KEY_PREFIX.length + 1);
    listener(userId, readCloneTasks(userId));
  };
  window.addEventListener('storage', onStorage);

  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
};

export const buildCloneTask = ({
  taskId,
  targetAccountId,
  targetAccountName,
  sourceAccountNames = [],
  machineRows = [],
  status
}) => ({
  taskId: String(taskId),
  targetAccountId: Number(targetAccountId),
  targetAccountName: targetAccountName || '',
  sourceAccountNames,
  status: normalizeMachineCloneStatus(status),
  errorMessage: '',
  startedAt: new Date().toISOString(),
  summary: {
    completed_machines: 0,
    total_machines: machineRows.length
  },
  machineRows
});
